import React, { useMemo } from 'react';
import moment from 'moment';

import Subfolder from './Subfolder';
// eslint-disable-next-line import/no-named-as-default
import useAuthorization from '../../hooks/useAuthorization';
import { ORG_ROLES } from '../../constants/main';

const SubfolderWithPermissions = ({
  subfolder,
  user,
  userRole,
  navigate,
  breadcrumbTo,
  handleUpdateTitle,
  handleShareFolder,
  handleCopyFolderLink,
  downloadFolder,
  handleDeleteFolder,
  handleMoveFolderToTrash,
  handleRestoreFolder,
  handleManageFolderUsers,
  handleMove,
}) => {
  // Use authorization hook for permission checks (similar to FolderRow)
  const resource = useMemo(() => ({ userId: subfolder.userId }), [subfolder.userId]);
  const resourceUser = useMemo(() => 
    userRole ? { role: userRole } : null, 
    [userRole]
  );
  
  // For subfolders, we check permissions based on the parent folder's userRole
  // Subfolders inherit permissions from parent folder
  const {
    canEditFolder,
    canDeleteFolder,
    canManageFolderPermissions,
    isFolderOwner,
    isOrgOwner,
  } = useAuthorization(
    user,
    resource,
    resourceUser,
    'folder'
  );

  const isNotAllowedToDeleteOrShare = !canDeleteFolder;
  const isNotAllowedToCopyLink = !canEditFolder; // Use centralized permission check - org owners can edit, so they can copy link
  const isTitleEditable = canEditFolder || subfolder.userId === user?.id;
  const isNotAllowedToManageUsers = !canManageFolderPermissions && !(resourceUser?.role === 'contributor');
  const isNotAllowedToDownload = user?.orgRole?.access !== ORG_ROLES.OWNER;

  // Calculate if any menu items will be visible
  // Menu items: Share, Edit, Manage Access, Delete, Copy link, Download
  // (Details, Viewer Report, Set Reminder are not passed, so they won't be visible)
  const hasVisibleMenuItems = useMemo(() => {
    // Share or Delete visible
    const hasShareOrDelete = canDeleteFolder;
    // Edit or Copy link visible
    const hasEditOrCopyLink = canEditFolder || subfolder.userId === user?.id;
    // Manage Access visible
    const hasManageAccess = canManageFolderPermissions;
    // Download visible
    const hasDownload = user?.orgRole?.access === ORG_ROLES.OWNER;

    return hasShareOrDelete || hasEditOrCopyLink || hasManageAccess || hasDownload;
  }, [canDeleteFolder, canEditFolder, canManageFolderPermissions, subfolder.userId, user?.id, user?.orgRole?.access]);

  return (
    <Subfolder
      hasUnviewedMeetings={subfolder.hasUnviewedMeetings}
      title={subfolder.name}
      onClick={() => navigate(`${breadcrumbTo}/${subfolder.id}`)}
      isTitleEditable={isTitleEditable}
      onUpdateTitle={(newTitle) => handleUpdateTitle(subfolder.id, newTitle)}
      hasActionsMenu={hasVisibleMenuItems}
      actionsMenuProps={{
        handleShare: () => handleShareFolder(subfolder),
        handleCopyLink: () => handleCopyFolderLink(subfolder.id),
        handleDownload: () => downloadFolder(subfolder),
        handleManageUsers: () => handleManageFolderUsers(subfolder),
        handleMove: handleMove && (isOrgOwner || isFolderOwner) ? () => handleMove(subfolder) : undefined,
        isNotAllowedToDelete: isNotAllowedToDeleteOrShare,
        isNotAllowedToShare: isNotAllowedToDeleteOrShare,
        isNotAllowedToManageUsers,
        isNotAllowedToCopyLink,
        isNotAllowedToDownload,
        handleDelete: () => handleDeleteFolder(subfolder),
        handleMoveToTrash: () => handleMoveFolderToTrash(subfolder),
        handleRestore: () => handleRestoreFolder(subfolder),
        isNotAllowedToEdit: !isTitleEditable,
      }}
      lastPublishedDate={moment(subfolder.updatedAt).format('MMM D, YYYY')}
    />
  );
};

export default SubfolderWithPermissions;

